/**
 * "What needs your attention" — the first-screen action shortlist (RFC-0002 C3).
 *
 * Three INDEPENDENT counts (never summed — no aggregate compliance score,
 * ADR-0002 red line): market-access blockers, items needing evidence, and new
 * discovered changes to review. Clicking through opens the Discovery & Review
 * surface. Advisory only — not a legal determination.
 */

import type { ComplianceRequirementCard } from "@regpulse/contracts";

interface Props {
  cards: ComplianceRequirementCard[];
  /** Discovered candidates awaiting review (computed by the parent from signals). */
  newToReviewCount: number;
  onOpenReview: () => void;
}

export function NeedsAttention({ cards, newToReviewCount, onOpenReview }: Props) {
  const applicable = cards.filter((c) => c.applicabilityCategory === "applicable");
  const blockers = applicable.filter((c) => c.legalEffect === "market_access_blocker").length;
  const needsEvidence = applicable.filter(
    (c) => c.coverageState === "missing" || c.coverageState === "partial",
  ).length;

  const stats: { key: string; testid: string; count: number; label: string }[] = [
    { key: "blockers", testid: "attn-blockers", count: blockers, label: "Market-access blockers" },
    { key: "evidence", testid: "attn-needs-evidence", count: needsEvidence, label: "Need evidence" },
    { key: "review", testid: "attn-new-review", count: newToReviewCount, label: "New to review" },
  ];

  return (
    <section
      data-testid="needs-attention"
      aria-labelledby="needs-attention-title"
      style={{
        background: "var(--color-surface-elevated)",
        border: "1px solid var(--color-border)",
        borderRadius: "var(--radius-8)",
        padding: "var(--space-12)",
      }}
    >
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: "var(--space-8)" }}>
        <h2 id="needs-attention-title" className="type-h3" style={{ margin: 0, fontSize: 14 }}>
          What needs your attention
        </h2>
        <button
          type="button"
          data-testid="attn-open-review"
          onClick={onOpenReview}
          style={{
            fontSize: 12,
            padding: "var(--space-4) var(--space-12)",
            border: "1px solid var(--color-accent)",
            background: "var(--color-accent-soft)",
            color: "var(--color-accent)",
            borderRadius: "var(--radius-full)",
            cursor: "pointer",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          <span aria-hidden="true" style={{ marginRight: 4 }}>
            🛎
          </span>
          Open Discovery &amp; Review
        </button>
      </header>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: "var(--space-8)",
          marginTop: "var(--space-12)",
        }}
      >
        {stats.map((s) => (
          <div
            key={s.key}
            data-testid={s.testid}
            data-count={s.count}
            style={{
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-6)",
              padding: "var(--space-8)",
              background: "var(--color-surface)",
            }}
          >
            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, fontFamily: "var(--font-mono)" }}>
              {s.count}
            </div>
            <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginTop: 2 }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      <p
        className="type-caption"
        style={{ margin: "var(--space-8) 0 0 0", color: "var(--color-text-disabled)", fontSize: 10 }}
      >
        Independent counts across applicable obligations — advisory, not a legal determination;
        each count stands alone, not a single combined figure (ADR-0002).
      </p>
    </section>
  );
}
